// skipcq: JS-W1028
/* eslint-disable react-hooks/set-state-in-effect -- dialog resets form when closed */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  BeerSourceFormPanel,
  defaultBeerSourceFormState,
} from './breweryBeerSourcePanels';

function BreweryBeerSourceFormDialog({
  open,
  onClose,
  breweryId,
  editingSource,
  initialUrl = '',
  actionError,
  actionLoading,
  onAddOrUpdateSource,
}) {
  const [formState, setFormState] = useState(defaultBeerSourceFormState);
  const editingSourceId = editingSource?.id ?? null;
  const dialogTitle = editingSourceId ? 'Edit Beer Source' : 'Add Beer Source';

  const resetForm = useCallback(() => {
    setFormState(defaultBeerSourceFormState);
  }, []);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (editingSource) {
      setFormState({
        url: editingSource.url || '',
        sourceCategory: editingSource.sourceCategory || 'official',
        sourceStructure: editingSource.sourceStructure || 'unknown',
        notes: editingSource.notes || '',
      });
      return;
    }
    setFormState({
      ...defaultBeerSourceFormState,
      url: initialUrl || '',
    });
  }, [open, editingSource, initialUrl, resetForm]);

  const handleFormFieldChange = useCallback((fieldName) => (event) => {
    setFormState((prev) => ({ ...prev, [fieldName]: event.target.value }));
  }, []);

  const handleSubmitSource = useCallback(async () => {
    const payload = {
      url: formState.url.trim(),
      sourceCategory: formState.sourceCategory,
      sourceStructure: formState.sourceStructure,
      notes: formState.notes.trim() || null,
    };
    if (editingSourceId) {
      payload.sourceId = editingSourceId;
      delete payload.url;
    }
    try {
      await onAddOrUpdateSource(payload);
      onClose();
    } catch {
      // actionError is surfaced by useBreweryBeerSources.runMutation
    }
  }, [editingSourceId, formState, onAddOrUpdateSource, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="beer-source-form-dialog-title"
      data-brewery-id={breweryId}
    >
      <DialogTitle id="beer-source-form-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent>
        <BeerSourceFormPanel
          actionError={actionError}
          actionLoading={actionLoading}
          editingSourceId={editingSourceId}
          formState={formState}
          onFormFieldChange={handleFormFieldChange}
          onResetForm={onClose}
          onSubmitSource={handleSubmitSource}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

BreweryBeerSourceFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  breweryId: PropTypes.string.isRequired,
  editingSource: PropTypes.object,
  initialUrl: PropTypes.string,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  onAddOrUpdateSource: PropTypes.func.isRequired,
};

export default BreweryBeerSourceFormDialog;
